// src/pages/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Box,
  Typography,
  Paper,
  CssBaseline,
  CircularProgress,
  Fade,
  Divider,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import logo from '../assets/tu_logo.png';
import backgroundImage from '../assets/login.jpeg';
import { InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery('(max-height: 600px)');
  const isExtraSmall = useMediaQuery('(max-height: 500px)');

  // Control: single-flight + cancelación + anti-spam
  const abortRef = useRef(null);
  const inFlightRef = useRef(false);
  const lastAttemptRef = useRef(0);

  
  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const doLoginRequest = async (controller) => {
    // Intenta login, con 1 retry en 429
    try {
      return await axiosInstance.post('/auth/login/', { username, password }, { signal: controller.signal });
    } catch (err) {
      const st = err?.response?.status;
      if (st === 429) {
        // Backoff breve y reintento único
        await sleep(1200);
        return await axiosInstance.post('/auth/login/', { username, password }, { signal: controller.signal });
      }
      throw err;
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current) return; // evita doble submit
    setFieldError('');

    // Anti-spam: mínimo 1s entre intentos
    const now = Date.now();
    if (now - lastAttemptRef.current < 1000) {
      toast.info('Demasiadas solicitudes seguidas. Espera un momento…');
      return;
    }
    lastAttemptRef.current = now;

    setLoading(true);
    inFlightRef.current = true;

    // Cancela intento anterior si lo hubiera
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await doLoginRequest(controller);
      const token = response?.data?.key || response?.data?.token;

      if (!token) {
        throw new Error('El servidor no devolvió token');
      }

      // Limpieza de otros tokens y seteo del de médico
      localStorage.setItem('authToken', token);
      localStorage.removeItem('patientToken');
      localStorage.removeItem('guestToken');
      localStorage.removeItem('isGuest');

      sessionStorage.setItem('justLoggedIn', 'true');

      toast.success('¡Has iniciado sesión correctamente!');
      await login(token); // Actualiza contexto con fetch controlado
      navigate('/doctor/dashboard', { replace: true });
    } catch (err) {
      if (controller.signal.aborted) return;

      const st = err?.response?.status;
      if (st === 429) {
        toast.error('Demasiados intentos. Espera unos segundos e inténtalo de nuevo.');
      } else if (st === 400 || st === 403 || st === 404) {
        setFieldError('El usuario o la contraseña son incorrectos.');
        toast.error('Error al iniciar sesión.');
      } else {
        console.error('Error de login:', err);
        toast.error('No se pudo iniciar sesión. Intenta nuevamente.');
      }
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  const handleSocialLogin = async (code) => {
    if (inFlightRef.current) return;
    setLoading(true);
    inFlightRef.current = true;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // 1 retry en 429
      const doSocial = async () => {
        try {
          return await axiosInstance.post('/auth/google/', { code }, { signal: controller.signal });
        } catch (err) {
          if (err?.response?.status === 429) {
            await sleep(1200);
            return await axiosInstance.post('/auth/google/', { code }, { signal: controller.signal });
          }
          throw err;
        }
      };

      const response = await doSocial();
      const token = response?.data?.key || response?.data?.token;

      localStorage.setItem('authToken', token);
      localStorage.removeItem('patientToken');
      localStorage.removeItem('guestToken');
      localStorage.removeItem('isGuest');

      sessionStorage.setItem('justLoggedIn', 'true');

      toast.success('¡Has iniciado sesión correctamente!');
      await login(token);
      navigate('/doctor/dashboard', { replace: true });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err?.response?.status === 429) {
        toast.error('Demasiadas solicitudes. Intenta nuevamente en unos segundos.');
      } else {
        toast.error('Error al iniciar sesión con Google.');
        console.error(err);
      }
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  // UI responsiva (mantiene tu lógica)
  const getResponsiveSizes = () => {
    if (isExtraSmall) {
      return {
        logoHeight: '40px',
        avatarSize: 24,
        iconSize: 'small',
        titleVariant: 'body1',
        inputSize: 'small',
        buttonPadding: 0.5,
        spacing: 0.5,
        linkVariant: 'caption',
        helperTextMargin: 0
      };
    } else if (isVerySmall) {
      return {
        logoHeight: '60px',
        avatarSize: 32,
        iconSize: 'small',
        titleVariant: 'subtitle1',
        inputSize: 'small',
        buttonPadding: 0.8,
        spacing: 1,
        linkVariant: 'caption',
        helperTextMargin: 0
      };
    } else {
      return {
        logoHeight: isMobile ? '80px' : '120px',
        avatarSize: 40,
        iconSize: 'medium',
        titleVariant: 'h5',
        inputSize: 'medium',
        buttonPadding: 1.5,
        spacing: 2,
        linkVariant: 'body2',
        helperTextMargin: 1
      };
    }
  };

  const sizes = getResponsiveSizes();

  const darkOverlay = isDarkMode
    ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1
        }
      }
    : {};

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        ...darkOverlay
      }}
    >
      <CssBaseline />
      <Fade in={true} timeout={1000}>
        <Box
          sx={{
            width: { xs: '90%', sm: '80%', md: '50%', lg: '40%' },
            maxWidth: '500px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: {
                xs: isExtraSmall ? 1 : isVerySmall ? 1.5 : 2,
                sm: isVerySmall ? 2 : 3,
                md: 4
              },
              borderRadius: '16px',
              backgroundColor: isDarkMode
                ? alpha(theme.palette.background.paper, 0.85)
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: theme.shadows[12],
              border: `1px solid ${isDarkMode ? alpha(theme.palette.divider, 0.2) : alpha(theme.palette.divider, 0.4)}`,
              overflow: 'hidden',
              aspectRatio: 'auto',
              maxHeight: 'none',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {!isExtraSmall && (
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    marginBottom: sizes.spacing / 2,
                    maxHeight: sizes.logoHeight,
                    objectFit: 'contain',
                    filter: isDarkMode ? 'brightness(0.9) contrast(1.1)' : 'none'
                  }}
                />
              )}

              <Avatar sx={{ m: sizes.spacing / 2, bgcolor: 'primary.main', width: sizes.avatarSize, height: sizes.avatarSize }}>
                <LockOutlinedIcon fontSize={sizes.iconSize} />
              </Avatar>

              <Typography component="h1" variant={sizes.titleVariant} fontWeight="bold" sx={{ mb: sizes.spacing / 2 }}>
                Iniciar Sesión
              </Typography>

              <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                <TextField
                  required
                  fullWidth
                  label="Nombre de Usuario"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  error={!!fieldError}
                  sx={{ mb: sizes.spacing / 2 }}
                  size={sizes.inputSize}
                  margin="dense"
                />

                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!fieldError}
                  helperText={fieldError || ' '}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      mt: sizes.helperTextMargin,
                      mb: 0
                    }
                  }}
                  size={sizes.inputSize}
                  margin="dense"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          onClick={() => setShowPassword((prev) => !prev)}
                          onMouseDown={(e) => e.preventDefault()} // Evita perder foco al mantener presionado
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton> 
                     </InputAdornment>
                    ), 
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: sizes.spacing, mb: sizes.spacing / 2, py: sizes.buttonPadding }}
                  size={sizes.inputSize}
                >
                  {loading ? <CircularProgress size={sizes.avatarSize * 0.6} color="inherit" /> : 'Entrar'}
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  component={RouterLink}
                  to="/register"
                  sx={{
                    mb: sizes.spacing,
                    py: sizes.buttonPadding,
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#388e3c' },
                  }}
                  size={sizes.inputSize}
                >
                  {isExtraSmall ? 'Regístrate' : '¿No tienes una cuenta? Regístrate'}
                </Button>

                <Divider sx={{ my: sizes.spacing / 2 }}>
                  <Typography variant={sizes.linkVariant} color="text.secondary">
                    O
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon fontSize={sizes.iconSize} />}
                  onClick={() => (window.location.href = `${API_BASE_URL}/accounts/google/login/?process=login`)}
                  disabled={loading}
                  sx={{ mt: sizes.spacing / 2, mb: sizes.spacing / 2, py: sizes.buttonPadding }}
                  size={sizes.inputSize}
                >
                  {isVerySmall ? 'Google' : 'Iniciar Sesión con Google'}
                </Button>

                <Divider sx={{ my: sizes.spacing / 2 }}>
                  <Typography variant={sizes.linkVariant} color="text.secondary">
                    Pacientes
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  component={RouterLink}
                  to="/patient/login"
                  sx={{ mt: sizes.spacing / 2, mb: sizes.spacing / 2, py: sizes.buttonPadding }}
                  size={sizes.inputSize}
                >
                  {isVerySmall ? 'Pacientes' : '¿Eres paciente? Inicia sesión aquí'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: sizes.spacing }}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    variant={sizes.linkVariant}
                    sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: isExtraSmall ? '0.7rem' : undefined }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Box>
  );
};

export default Login;
